import { useEffect, useState } from 'react';
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Database,
  Eraser,
  FileInput,
  FileSearch,
  FlaskConical,
  Menu,
  Presentation,
  X,
} from 'lucide-react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { cn } from '@/utils/cn';
import { showToast } from '@/utils/toast';
import { PriceIntelModeSwitch } from './components/PriceIntelModeSwitch';
import { usePriceIntelStore } from './stores/price-intel-store';

type NavItem = {
  to: string;
  label: string;
  icon: typeof Database;
  end?: boolean;
  badgeKey?: 'review' | 'jobs' | 'facts' | 'briefings';
};

type NavGroup = { title: string; items: NavItem[] };

const navGroups: NavGroup[] = [
  {
    title: '',
    items: [
      { to: '/price_intel', label: '录入', icon: FileInput, end: true },
      { to: '/price_intel/doc_hub', label: '文档中枢', icon: FileSearch },
      { to: '/price_intel/database', label: '数据库', icon: Database },
      { to: '/price_intel/analytics', label: '分析', icon: BarChart3 },
      { to: '/price_intel/reports', label: 'AI 报告', icon: Presentation, badgeKey: 'briefings' },
    ],
  },
];


function getCurrentTitle(pathname: string): string {
  for (const group of navGroups) {
    for (const item of group.items) {
      if (item.end ? pathname === item.to : pathname.startsWith(item.to) && item.to !== '/price_intel') {
        return item.label;
      }
    }
  }
  return '总览';
}

export function PriceIntelLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false);

  const {
    mode,
    jobs,
    candidates,
    facts,
    briefings,
    seedDemoData,
    clearDemoData,
  } = usePriceIntelStore();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const counters = {
    review: candidates.length,
    jobs: jobs.filter((j) => j.status === 'queued' || j.status === 'running').length,
    facts: facts.length,
    briefings: briefings.length,
  };

  const currentTitle = getCurrentTitle(location.pathname);

  const handleSeed = async () => {
    try {
      await seedDemoData();
      showToast('演示样例已注入');
    } catch (error) {
      showToast(`操作失败: ${(error as Error).message}`);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('清空当前浏览器中的本地价格情报数据？')) return;
    try {
      await clearDemoData();
      showToast('本地数据已清空');
    } catch (error) {
      showToast(`操作失败: ${(error as Error).message}`);
    }
  };

  const sidebar = (
    <aside
      className={cn(
        'flex h-full w-[240px] shrink-0 flex-col border-r transition-[width] duration-200',
        desktopSidebarCollapsed && 'lg:w-[60px]',
      )}
      style={{
        backgroundColor: 'var(--color-background-primary)',
        borderColor: 'var(--color-system-quaternary)',
      }}
    >
      <div
        className={cn(
          'flex h-[56px] items-center justify-between border-b px-5',
          desktopSidebarCollapsed && 'lg:justify-center lg:px-2',
        )}
        style={{ borderColor: 'var(--color-system-quaternary)' }}
      >
        <div className={cn('flex flex-col leading-tight', desktopSidebarCollapsed && 'lg:hidden')}>
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.22em]"
            style={{ color: 'var(--color-system-secondary)' }}
          >
            Price Intel
          </span>
          <span
            className="text-[15px] font-semibold"
            style={{
              background: 'linear-gradient(135deg, var(--color-key) 0%, #667eea 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            竞品情报
          </span>
        </div>
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="rounded-md p-1.5 transition hover:bg-[var(--color-background-secondary)] lg:hidden"
          style={{ color: 'var(--color-system-secondary)' }}
          aria-label="关闭菜单"
        >
          <X size={16} />
        </button>
        <button
          type="button"
          onClick={() => setDesktopSidebarCollapsed((collapsed) => !collapsed)}
          className="hidden rounded-md p-1.5 transition hover:bg-[var(--color-background-secondary)] lg:inline-flex"
          style={{ color: 'var(--color-system-secondary)' }}
          aria-label={desktopSidebarCollapsed ? '展开竞品情报导航' : '折叠竞品情报导航'}
          title={desktopSidebarCollapsed ? '展开导航栏' : '折叠导航栏'}
        >
          {desktopSidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {navGroups.map((group) => (
          <div key={group.title || group.items[0]?.to || 'misc'} className="mb-5">
            {group.title && (
            <div
              className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-[0.22em]"
              style={{ color: 'var(--color-system-tertiary)' }}
            >
              {group.title}
            </div>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const badgeValue = item.badgeKey ? counters[item.badgeKey] : 0;
                const showBadge = item.badgeKey && badgeValue > 0;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cn(
                        'group flex items-center justify-between rounded-lg px-2.5 py-2 text-sm font-medium transition',
                        desktopSidebarCollapsed && 'lg:justify-center lg:px-2',
                        !isActive && 'hover:bg-[var(--color-background-secondary)]',
                      )
                    }
                    title={desktopSidebarCollapsed ? item.label : undefined}
                    style={({ isActive }) =>
                      isActive
                        ? {
                            backgroundColor: 'rgba(0, 113, 227, 0.1)',
                            color: 'var(--color-key)',
                          }
                        : { color: 'var(--color-system-secondary)' }
                    }
                  >
                    <span className="flex items-center gap-2.5">
                      <item.icon size={16} />
                      <span className={cn(desktopSidebarCollapsed && 'lg:hidden')}>{item.label}</span>
                    </span>
                    {showBadge && (
                      <span
                        className={cn(
                          'inline-flex min-w-[20px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                          desktopSidebarCollapsed && 'lg:hidden',
                        )}
                        style={{
                          backgroundColor:
                            item.badgeKey === 'review'
                              ? 'rgba(255, 149, 0, 0.15)'
                              : 'var(--color-background-secondary)',
                          color:
                            item.badgeKey === 'review'
                              ? 'var(--color-warning)'
                              : 'var(--color-system-secondary)',
                        }}
                      >
                        {badgeValue}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div
        className={cn('border-t px-3 py-3', desktopSidebarCollapsed && 'lg:px-2')}
        style={{ borderColor: 'var(--color-system-quaternary)' }}
      >
        {mode === 'local' && (
          <div className={cn('mb-2 flex gap-1.5', desktopSidebarCollapsed && 'lg:hidden')}>
            <button
              onClick={() => void handleSeed()}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-xs font-medium transition hover:opacity-80"
              style={{
                borderColor: 'var(--color-system-quaternary)',
                color: 'var(--color-system-secondary)',
              }}
            >
              <FlaskConical size={12} />
              样例
            </button>
            <button
              onClick={() => void handleClear()}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-xs font-medium transition hover:opacity-80"
              style={{
                borderColor: 'var(--color-system-quaternary)',
                color: 'var(--color-system-secondary)',
              }}
            >
              <Eraser size={12} />
              清空
            </button>
          </div>
        )}
        <button
          onClick={() => navigate('/uadmin')}
          className={cn(
            'flex w-full items-center gap-2 rounded-md px-2 py-2 text-xs font-medium transition hover:bg-[var(--color-background-secondary)]',
            desktopSidebarCollapsed && 'lg:justify-center',
          )}
          style={{ color: 'var(--color-system-secondary)' }}
          title={desktopSidebarCollapsed ? '返回文件系统' : undefined}
        >
          <ChevronLeft size={14} />
          <span className={cn(desktopSidebarCollapsed && 'lg:hidden')}>返回文件系统</span>
        </button>
      </div>
    </aside>
  );

  return (
    <div
      className="flex h-screen overflow-hidden"
      style={{
        backgroundColor: 'var(--color-background-secondary)',
        color: 'var(--color-system-primary)',
        fontFamily: 'var(--font-family-system)',
      }}
    >
      <div className="hidden h-screen shrink-0 lg:block">{sidebar}</div>

      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/30 lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden">{sidebar}</div>
        </>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header
          className="flex h-[56px] shrink-0 items-center justify-between border-b px-4 lg:px-6"
          style={{
            backgroundColor: 'var(--color-background-primary)',
            borderColor: 'var(--color-system-quaternary)',
          }}
        >
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="rounded-md p-1.5 transition hover:bg-[var(--color-background-secondary)] lg:hidden"
              style={{ color: 'var(--color-system-secondary)' }}
              aria-label="打开菜单"
            >
              <Menu size={18} />
            </button>
            <div className="flex items-center gap-2 text-sm">
              <span style={{ color: 'var(--color-system-tertiary)' }}>情报平台</span>
              <span style={{ color: 'var(--color-system-tertiary)' }}>/</span>
              <span className="font-medium" style={{ color: 'var(--color-system-primary)' }}>
                {currentTitle}
              </span>
            </div>
          </div>

          <PriceIntelModeSwitch />
        </header>

        <main className="min-h-0 flex-1 overflow-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
